import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "./styles.css";
import LlmIcon from "../LlmIcon";
import MicIcon from "../MicIcon";
import ThinkingIcon from "../ThinkingIcon";
import CheckIcon from "../CheckIcon";
import Button from "../Button";
import { thePrompt, selectAtoms, speakup } from "./utils";
import ListeningIcon from "../ListeningIcon";

let keepLoggingTraj = false;
let trajInterval = null;

const containerVariants = {
  initial: {
    opacity: 0,
    scale: 0.8,
    y: "calc(-50% + 50px)",
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: "-50%",
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    y: "calc(-50% + 50px)",
    transition: {
      duration: 0.2,
    },
  },
};

const examples = [
  "e.g. Show positively charged residues in blue",
  "e.g. Pause simulation",
  "e.g. Write the coordinates",
  "e.g. Color chain A as rainbow",
  "e.g. How many atoms are there?",
  "e.g. Show nucleic acids as sticks and proteins as cartoons, all colored by chain",
  "e.g. Hide all hydrogens",
  "e.g. I want to see the aromatic amino acids in purple",
  "e.g. Make molecules bigger",
  "e.g. Change temperature to 30 Celsius",
  "e.g. Increase the temperature a little bit",
  "e.g. Who developed this?",
  "Note: Speech recognition only in Chrome browsers, in English",
  "Note: Typed requests and questions in any browser and language",
];

const MODULE_STATES = {
  TEXT_INPUT: "text_input",
  AUDIO_INPUT: "audio_input",
  THINKING: "thinking",
  RESULTS: "results",
};

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const API_KEY = process.env.REACT_APP_OPENAI_KEY;
let commandsHistory = [];
let currentlySelectedAtomIndexes = [];
let commandsHistoryNavigation = -1;
export let recognition;

const LlmModule = ({
  onFocus,
  onBlur,
  onMoleculeScale,
  onUpdateRates,
  onTemperatureChange,
  onTemperatureSet,
  onSetAtomRadiusFactors,
  onSetAtomColors,
  onSetStickRadius,
  onWritePDB,
  onSetSelection,
  onStopSim,
  onStartSim,
  onMolRotationSetByValue,
  onMolRotationChange,
  onToggleLlmActive,
  isActive,
  pdb,
}) => {
  const [hasSpeech, setHasSpeech] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [promptString, setPromptString] = useState("");
  const [response, setResponse] = useState("");
  const [speechText, setSpeechText] = useState("");
  const [moduleState, setModuleState] = useState(MODULE_STATES.TEXT_INPUT);
  const [previousInputState, setPreviousInputState] = useState(MODULE_STATES.TEXT_INPUT);
  const [exampleIndex, setExampleIndex] = useState(Math.floor(Math.random() * examples.length));

  useEffect(() => {
    if (isActive !== false) {
      onToggleLlmActive(false);
    }
  }, []);

  useEffect(() => {
    if (typeof SpeechRecognition === "undefined") {
      console.log("Speech API failed");
    } else {
      console.log("Speech API ok");
      setHasSpeech(true);
      recognition = new SpeechRecognition();

      //Block that adds specific words -doesn't really work well at least in chrome, see all discussion online
      const grammar = `
          #JSGF V1.0;
          grammar commands;
          public <command> = residue | residues | amino acid | protein | nucleic acid | membrane | minimize | serine | cpk | zoom in | handmol;
          `;
      //More grammar from older system: "#JSGF V1.0; grammar colors; public <color> = forcefield | force field | minimize with ani | relax with ani ;";

      let SpeechGrammarList = window.SpeechGrammarList || window.webkitSpeechGrammarList;
      const speechRecognitionList = new SpeechGrammarList();
      speechRecognitionList.addFromString(grammar, 1);
      recognition.grammars = speechRecognitionList;

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US"; // Use the appropriate BCP-47 code

      recognition.onresult = (event) => {
        const last = event.results.length - 1;
        const res = event.results[last];
        const text = res[0].transcript;

        if (res.isFinal && text !== "") {
          console.log("Recognized speech:\n\n" + text + "\n\n");
          setSpeechText(text);
          recognition.stop();
          setIsListening(false);
          setPreviousInputState(MODULE_STATES.AUDIO_INPUT);

          setTimeout(function () {
            speechToCommands(text.trim(), MODULE_STATES.AUDIO_INPUT);
          }, Math.min(500 + 100 * parseInt(text.length), 5000)); //CHANGED from 1500
        } else {
          setSpeechText(text);
          setIsListening(true);
        }
      };
    }
  }, []);

  useEffect(() => {
    if (hasSpeech) {
      if (moduleState === MODULE_STATES.AUDIO_INPUT) {
        recognition.start();
        setIsListening(true);
        setPreviousInputState(MODULE_STATES.AUDIO_INPUT);
      } else {
        recognition.stop();
        setIsListening(false);
      }
    }
  }, [hasSpeech, moduleState]);

  useEffect(() => {
    let timer;
    let duration = response === "" ? 1000 : 1500;
    if (moduleState === MODULE_STATES.RESULTS) {
      timer = setTimeout(() => {
        if (previousInputState === MODULE_STATES.AUDIO_INPUT) {
          setModuleState(MODULE_STATES.AUDIO_INPUT);
        } else {
          setModuleState(MODULE_STATES.TEXT_INPUT);
        }
        setResponse("");
        setSpeechText("");
      }, Math.min(500 + 100 * parseInt(response.length), 10000)); //CHANGED from duration
    }
    return () => clearTimeout(timer);
  }, [moduleState, previousInputState]);

  useEffect(() => {
    const interval = setInterval(() => {
      setExampleIndex((prev) => (prev + 1) % examples.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleSendPrompt = () => {
    onBlur();
    if (promptString.length > 0) {
      speechToCommands(promptString, MODULE_STATES.TEXT_INPUT);
      commandsHistory.push(promptString);
      setPromptString("");
    }
  };

  function speechToCommands(usersRequest, inputSource) {
    // Store the input source (audio or text)
    const isAudioInput = inputSource === MODULE_STATES.AUDIO_INPUT;

    if (usersRequest.length < 500 || usersRequest.substring(0, 7) === "script:") {
      if (usersRequest.substring(0, 7) === "script:") {
        let usersScript = usersRequest.substring(7).trim();
        console.log("User provided this script, running directly:\n\n" + usersScript);
        runUSersScript(usersScript);
        setModuleState(MODULE_STATES.RESULTS);
      } else {
        let thepromptplusrequest = thePrompt.slice();
        thepromptplusrequest.push({
          role: "user",
          content: usersRequest + ". Remember to show a comment explaining the code.",
        });

        console.log("Sending this to LLM:\n\n" + usersRequest + ". Remember to show a comment explaining the code.");

        setModuleState(MODULE_STATES.THINKING);

        fetch(`https://api.openai.com/v1/chat/completions`, {
          body: JSON.stringify({
            model: "gpt-4o-mini",
            messages: thepromptplusrequest,
            temperature: 0,
            max_tokens: 500,
          }),
          method: "POST",
          headers: {
            "content-type": "application/json",
            Authorization: "Bearer  " + API_KEY,
          },
        })
          .then((response) => {
            if (response.ok) {
              response.json().then((json) => {
                let comandos = json.choices[0].message.content.trim();
                console.log("Commands from LLM a:\n" + comandos);
                thePrompt.push({ role: "user", content: usersRequest });
                thePrompt.push({ role: "assistant", content: comandos });

                comandos = comandos.endsWith(";") ? comandos.slice(0, -1) : comandos;
                comandos = comandos.replaceAll("\n", ";");
                comandos = comandos.split(";");

                // Variables to store response and speech text
                let textToSpeak = "";
                let responseText = "";

                for (let c = 0; c < comandos.length; c++) {
                  let comando = comandos[c].trim();
                  console.log("Command " + (c + 1) + ": " + comando);

                  if (comando === "didntUnderstand()") {
                    responseText = "Didn't understand the command";
                  } else if (comando.substring(0, 2) == "//") {
                    console.log("Comment line: " + comando);
                  } else if (comando == "sayTime()") {
                    var currentTime = new Date();
                    textToSpeak =
                      "It's " + currentTime.getHours() + " hours and " + currentTime.getMinutes() + " minutes.";
                    responseText = "Time: " + currentTime.getHours() + ":" + currentTime.getMinutes();
                  } else if (comando == "sayDate()") {
                    const dateText =
                      "Today is " +
                      (new Date().getMonth() + 1) +
                      "/" +
                      new Date().getDate() +
                      "/" +
                      new Date().getFullYear() +
                      ".";
                    textToSpeak = dateText;
                    responseText = dateText;
                  } else if (comando.substring(0, 7) == "speakUp") {
                    const speakText = comando.substring(9, comando.length - 2);
                    textToSpeak = speakText;
                    responseText = speakText;
                  } else if (comando == "countAtoms()") {
                    textToSpeak = "There are " + pdb.names.length + " atoms in this system.";
                    responseText =
                      pdb.names.length +
                      " atoms in system.\n\nIf this number is different to that in your input file, consider that certain atoms are added or removed during simulation setup. For example, for AMBER simulations all H atoms are added; and for CALVADOS the atoms are condensed into CG beads.";
                  } else if (comando == "greetHello()") {
                    textToSpeak = "Hello and welcome to HandMol.";
                    responseText = "Welcome to HandMol!";
                  } else if (comando == "greetBye()") {
                    textToSpeak = "Goodbye!";
                    responseText = textToSpeak;
                  } else if (comando.substring(0, 11) == "acknowledge") {
                    if (comando.length > 13) {
                      textToSpeak = comando.substring(13, comando.length - 2);
                      responseText = textToSpeak;
                    }
                  } else if (comando.substring(0, 6) == "zoomIn") {
                    onMoleculeScale("up");
                  } else if (comando.substring(0, 7) == "zoomOut") {
                    onMoleculeScale("down");
                  } else if (comando.substring(0, 17) == "changeTemperature") {
                    let deltaTemperature = parseFloat(comando.substring(18, comando.length - 1));
                    onTemperatureChange(deltaTemperature);
                    if (deltaTemperature > 0) {
                      responseText = "Attempting to increase temperature by " + deltaTemperature + " K.";
                    } else if (deltaTemperature < 0) {
                      responseText = "Attempting to decrease temperature by " + Math.abs(deltaTemperature) + " K.";
                    }
                  } else if (comando.substring(0, 16) == "changeUpdateRate") {
                    let howToChangeUpdateRate = comando.substring(17, comando.length - 1);
                    console.log(howToChangeUpdateRate);
                    if (howToChangeUpdateRate > 0) {
                      onUpdateRates("up");
                      responseText =
                        "Increased data exchange rate.\n\nIf this doesn't make the visualization more fluid, your WiFi connection may be weak and/or your system's size is near the supported limit.'";
                    }
                    if (howToChangeUpdateRate < 0) {
                      onUpdateRates("down");
                      responseText = "Decreased data exchange rate.";
                    }
                  } else if (comando.substring(0, 9) == "rotateTo(") {
                    //let angles = comando.substring(9).trim().replace(")","").split(",");
                    let angles = comando.substring(9)
                      .trim()
                      .replace(")", "")
                      .split(",")
                      .map(angle => parseFloat(angle.trim()));  // Trim spaces and convert to floats
                    let RotationsObject = {
                      rotX: angles[0],
                      rotY: angles[1],
                      rotZ: angles[2],
                    };
                    onMolRotationSetByValue(RotationsObject);
                  } else if (comando.substring(0, 7) == "rotate(") {
                    //let angles = comando.substring(7).trim().replace(")","").split(",");
                    let angles = comando.substring(7)
                      .trim()
                      .replace(")", "")
                      .split(",")
                      .map(angle => parseFloat(angle.trim()));  // Trim spaces and convert to floats
                    let RotationsObject = {
                      rotX: angles[0],
                      rotY: angles[1],
                      rotZ: angles[2],
                    };
                    onMolRotationChange(RotationsObject);
                  } else if (comando.substring(0, 14) == "setTemperature") {
                    let newTemperature = parseFloat(comando.substring(15, comando.length - 1));
                    onTemperatureSet(newTemperature);
                    responseText = "Attempting to set temperature to " + newTemperature + " K...";
                  } else if (comando.substring(0, 15) == "startSimulation") {
                    onStartSim();
                    responseText =
                      "Starting simulation...\n\nIf atoms don't move, consider the possibility that the temperature is zero Kelvin.";
                  } else if (comando.substring(0, 14) == "stopSimulation") {
                    onStopSim();
                    responseText = "Pausing simulation...";
                  } else if (comando.substring(0, 6) == "select") {
                    responseText = "Applying the following selection: " + comando.substring(8, comando.length - 2);
                    currentlySelectedAtomIndexes = selectAtoms(comando.substring(8, comando.length - 2), pdb);
                    console.log("Selected these indexes: " + currentlySelectedAtomIndexes);
                  } else if (comando.substring(0, 8) == "writePDB") {
                    onWritePDB();
                    textToSpeak = "Writing PDB. It will show up in your downloads.";
                    responseText = textToSpeak;
                  } else if (comando.substring(0, 13) == "logTrajectory") {
                    const argumento = comando.match(/logTrajectory\((.*?)\)/)[1].trim();

                    if (argumento === "FALSE") {
                      if (trajInterval !== null) {
                        clearInterval(trajInterval);
                        trajInterval = null;
                      }
                      keepLoggingTraj = false;
                    } else {
                      console.log("Starting to log trajectory as separate PDB files that can be joined with a cat command in Linux.");
                      keepLoggingTraj = true;
                      trajInterval = setInterval(function () {
                        if (!keepLoggingTraj) return;

                        onWritePDB();

            /*            trajectoryModelNo++;
                        trajectoryString += "MODEL       " + trajectoryModelNo + "\n";

                        for (let i = 0; i !== currentPdb .names.length; i++) {
                          let xx = currentPdb.positions[i].x;
                          let yy = currentPdb.positions[i].y;
                          let zz = currentPdb.positions[i].z;

                          //let yy = (atomBodies[i].position.y + mediay - translation.y) / globalScale;

                          if (i > 0) {
                            if (currentPdb.chains[i - 1] !== currentPdb.chains[i]) {
                              //add TER for Amber
                              trajectoryString = trajectoryString + "TER\n";
                            }
                          }

                          trajectoryString =
                            trajectoryString +
                            "ATOM " +
                            " ".repeat(6 - currentPdb.atomnumbers[i].toString().length) +
                            currentPdb.atomnumbers[i] +
                            " " +
                            " ".repeat(4 - currentPdb.names[i].length) +
                            currentPdb.names[i] +
                            " ".repeat(4 - currentPdb.resnames[i].length) +
                            currentPdb.resnames[i] +
                            " " +
                            currentPdb.chains[i] +
                            " ".repeat(4 - currentPdb.resnos[i].toString().length) +
                            currentPdb.resnos[i] +
                            "    " +
                            " ".repeat(8 - xx.toFixed(3).toString().length) +
                            xx.toFixed(3).toString() +
                            " ".repeat(8 - yy.toFixed(3).toString().length) +
                            yy.toFixed(3).toString() +
                            " ".repeat(8 - zz.toFixed(3).toString().length) +
                            zz.toFixed(3).toString() +
                            "  1.00  0.00         " +
                            " ".repeat(3 - currentPdb.elements[i].length) +
                            currentPdb.elements[i] +
                            "\n";
                        }
                        trajectoryString = trajectoryString + "\nTER\nENDMDL\n";
                        console.log("Added frame to trajectory; will be saved when logging is finished with logTrajectory(FALSE)");   */

                      }, Math.max(1000, parseInt(argumento, 10)));
                    }
                    //textToSpeak = "Writing PDB. It will show up in your downloads.";
                    //responseText = textToSpeak;
                  } else if (comando.substring(0, 9) == "spacefill") {
                    if (currentlySelectedAtomIndexes.length > 0) {
                      onSetAtomRadiusFactors([
                        parseFloat(comando.substring(9, comando.length)),
                        currentlySelectedAtomIndexes,
                      ]);
                    }
                  } else if (comando.substring(0, 6) == "sticks") {
                    if (currentlySelectedAtomIndexes.length > 0) {
                      onSetStickRadius([
                        parseFloat(comando.substring(6, comando.length)),
                        currentlySelectedAtomIndexes,
                      ]);
                    }
                  } else if (comando.substring(0, 5) == "color") {
                    let newColor = comando.substring(6, comando.length).trim();
                    if (currentlySelectedAtomIndexes.length > 0) {
                      onSetAtomColors([newColor, currentlySelectedAtomIndexes]);
                    }
                  }
                }

                // Set the response text if it's not empty
                if (responseText) {
                  setResponse(responseText);
                }

                // Speak the text if coming from audio input and we have text to speak
                if (isAudioInput && textToSpeak) {
                  speakup(textToSpeak);
                }

                setModuleState(MODULE_STATES.RESULTS);
              });
            } else {
              console.log("Error connecting to language model or problem with API key. Please contact the developers.");
              const errorText =
                "Error connecting to language model or problem with API key. Please contact the developers.";
              if (isAudioInput) speakup(errorText);
              setResponse(
                "Error connecting to language model.\n\nPlease contact the developers at luciano_abriata@yahoo.com"
              );
              setModuleState(MODULE_STATES.RESULTS);
            }
          })
          .catch((error) => {
            console.error("Error:", error);
            setResponse("An error occurred while processing your request.");
            setModuleState(previousInputState);
          });
      }
    } else {
      console.log("User's prompt was too long.");
      const tooLongText = "User's prompt was too long.";
      if (isAudioInput) speakup(tooLongText);
      setResponse(
        "User's prompt was too long.\n\nIf your intentions were honest, please contact the developers at luciano_abriata@yahoo.com"
      );
      setModuleState(previousInputState);
    }
  }

  function runUSersScript(comandos) {
    console.log("Running these Commands: " + comandos);
    comandos = comandos.endsWith(";") ? comandos.slice(0, -1) : comandos;
    comandos = comandos.split(";");

    // Variables to store response and speech text
    let textToSpeak = "";
    let responseText = "";

    for (let c = 0; c < comandos.length; c++) {
      let comando = comandos[c].trim();
      console.log("Command " + (c + 1) + ": " + comando);

      if (comando === "didntUnderstand()") {
        responseText = "Didn't understand the command";
      } else if (comando == "sayTime()") {
        var currentTime = new Date();
        textToSpeak = "It's " + currentTime.getHours() + " hours and " + currentTime.getMinutes() + " minutes.";
        responseText = "Time: " + currentTime.getHours() + ":" + currentTime.getMinutes();
      } else if (comando == "sayDate()") {
        const dateText =
          "Today is " + (new Date().getMonth() + 1) + "/" + new Date().getDate() + "/" + new Date().getFullYear() + ".";
        textToSpeak = dateText;
        responseText = dateText;
      } else if (comando.substring(0, 7) == "speakUp") {
        const speakText = comando.substring(9, comando.length - 2);
        textToSpeak = speakText;
        responseText = speakText;
      } else if (comando == "countAtoms()") {
        textToSpeak = "I've loaded " + pdb.names.length + " atoms for this system.";
        responseText =
          pdb.names.length +
          " atoms in system.\n\nIf this number is different to that in your input file, consider that certain atoms are added or removed during simulation setup. For example, for AMBER simulations all H atoms are added; and for CALVADOS the atoms are condensed into CG beads.";
      } else if (comando == "greetHello()") {
        textToSpeak = "Hello and welcome to HandMol.";
        responseText = "Welcome to HandMol!";
      } else if (comando == "greetBye()") {
        textToSpeak = "Goodbye!";
        responseText = textToSpeak;
      } else if (comando.substring(0, 11) == "acknowledge") {
        if (comando.length > 13) {
          textToSpeak = comando.substring(13, comando.length - 2);
          responseText = textToSpeak;
        }
      } else if (comando.substring(0, 6) == "zoomIn") {
        onMoleculeScale("up");
      } else if (comando.substring(0, 7) == "zoomOut") {
        onMoleculeScale("down");
      } else if (comando.substring(0, 17) == "changeTemperature") {
        let deltaTemperature = parseFloat(comando.substring(18, comando.length - 1));
        onTemperatureChange(deltaTemperature);
        if (deltaTemperature > 0) {
          responseText = "Attempting to increase temperature by " + deltaTemperature + " K.";
        } else if (deltaTemperature < 0) {
          responseText = "Attempting to decrease temperature by " + Math.abs(deltaTemperature) + " K.";
        }
      } else if (comando.substring(0, 16) == "changeUpdateRate") {
        let howToChangeUpdateRate = comando.substring(17, comando.length - 1);
        console.log(howToChangeUpdateRate);
        if (howToChangeUpdateRate == 1) {
          onUpdateRates("up");
          responseText =
            "Increased data exchange rate.\n\nIf this doesn't make the visualization more fluid, your WiFi connection may be weak and/or your system's size is near the supported limit.'";
        }
        if (howToChangeUpdateRate == -1) {
          onUpdateRates("down");
          responseText = "Decreased data exchange rate.";
        }
      } else if (comando.substring(0, 9) == "rotateTo(") {
        //let angles = comando.substring(9).trim().replace(")","").split(",");
        let angles = comando.substring(9)
          .trim()
          .replace(")", "")
          .split(",")
          .map(angle => parseFloat(angle.trim()));  // Trim spaces and convert to floats
        let RotationsObject = {
          rotX: angles[0],
          rotY: angles[1],
          rotZ: angles[2],
        };
        onMolRotationSetByValue(RotationsObject);
      } else if (comando.substring(0, 7) == "rotate(") {
        //let angles = comando.substring(7).trim().replace(")","").split(",");
        let angles = comando.substring(7)
          .trim()
          .replace(")", "")
          .split(",")
          .map(angle => parseFloat(angle.trim()));  // Trim spaces and convert to floats
        let RotationsObject = {
          rotX: angles[0],
          rotY: angles[1],
          rotZ: angles[2],
        };
        onMolRotationChange(RotationsObject);
      } else if (comando.substring(0, 14) == "setTemperature") {
        let newTemperature = parseFloat(comando.substring(15, comando.length - 1));
        onTemperatureSet(newTemperature);
        responseText = "Attempting to set temperature to " + newTemperature + " K...";
      } else if (comando.substring(0, 15) == "startSimulation") {
        onStartSim();
        responseText =
          "Starting simulation...\n\nIf atoms don't move, consider the possibility that the temperature is zero Kelvin.";
      } else if (comando.substring(0, 14) == "stopSimulation") {
        onStopSim();
        responseText = "Pausing simulation...";
      } else if (comando.substring(0, 6) == "select") {
        responseText = "Applying the following selection: " + comando.substring(8, comando.length - 2);
        currentlySelectedAtomIndexes = selectAtoms(comando.substring(8, comando.length - 2), pdb);
        console.log("Selected these indexes: " + currentlySelectedAtomIndexes);
      } else if (comando.substring(0, 8) == "writePDB") {
        onWritePDB();
        textToSpeak = "Writing PDB. It will show up in your downloads.";
        responseText = textToSpeak;
      } else if (comando.substring(0, 13) == "logTrajectory") {
        const argumento = comando.match(/logTrajectory\((.*?)\)/)[1].trim();

        if (argumento === "FALSE") {
          if (trajInterval !== null) {
            clearInterval(trajInterval);
            trajInterval = null;
          }
          keepLoggingTraj = false;
        } else {
          console.log("Starting to log trajectory as separate PDB files that can be joined with a cat command in Linux.");
          keepLoggingTraj = true;
          trajInterval = setInterval(function () {
            if (!keepLoggingTraj) return;

            onWritePDB();

/*            trajectoryModelNo++;
            trajectoryString += "MODEL       " + trajectoryModelNo + "\n";

            for (let i = 0; i !== currentPdb .names.length; i++) {
              let xx = currentPdb.positions[i].x;
              let yy = currentPdb.positions[i].y;
              let zz = currentPdb.positions[i].z;

              //let yy = (atomBodies[i].position.y + mediay - translation.y) / globalScale;

              if (i > 0) {
                if (currentPdb.chains[i - 1] !== currentPdb.chains[i]) {
                  //add TER for Amber
                  trajectoryString = trajectoryString + "TER\n";
                }
              }

              trajectoryString =
                trajectoryString +
                "ATOM " +
                " ".repeat(6 - currentPdb.atomnumbers[i].toString().length) +
                currentPdb.atomnumbers[i] +
                " " +
                " ".repeat(4 - currentPdb.names[i].length) +
                currentPdb.names[i] +
                " ".repeat(4 - currentPdb.resnames[i].length) +
                currentPdb.resnames[i] +
                " " +
                currentPdb.chains[i] +
                " ".repeat(4 - currentPdb.resnos[i].toString().length) +
                currentPdb.resnos[i] +
                "    " +
                " ".repeat(8 - xx.toFixed(3).toString().length) +
                xx.toFixed(3).toString() +
                " ".repeat(8 - yy.toFixed(3).toString().length) +
                yy.toFixed(3).toString() +
                " ".repeat(8 - zz.toFixed(3).toString().length) +
                zz.toFixed(3).toString() +
                "  1.00  0.00         " +
                " ".repeat(3 - currentPdb.elements[i].length) +
                currentPdb.elements[i] +
                "\n";
            }
            trajectoryString = trajectoryString + "\nTER\nENDMDL\n";
            console.log("Added frame to trajectory; will be saved when logging is finished with logTrajectory(FALSE)");   */

          }, Math.max(1000, parseInt(argumento, 10)));
        }
        //textToSpeak = "Writing PDB. It will show up in your downloads.";
        //responseText = textToSpeak;
      } else if (comando.substring(0, 9) == "spacefill") {
        if (currentlySelectedAtomIndexes.length > 0) {
          onSetAtomRadiusFactors([parseFloat(comando.substring(9, comando.length)), currentlySelectedAtomIndexes]);
        }
      } else if (comando.substring(0, 6) == "sticks") {
        if (currentlySelectedAtomIndexes.length > 0) {
          onSetStickRadius([parseFloat(comando.substring(6, comando.length)), currentlySelectedAtomIndexes]);
        }
      } else if (comando.substring(0, 5) == "color") {
        let newColor = comando.substring(6, comando.length).trim();
        if (currentlySelectedAtomIndexes.length > 0) {
          onSetAtomColors([newColor, currentlySelectedAtomIndexes]);
        }
      }
    }

    // Set the response text if it's not empty
    if (responseText) {
      setResponse(responseText);
    }

    /*// Speak the text if coming from audio input and we have text to speak   NO AUDIO OUTPUT IN THIS CASE
    if (isAudioInput && textToSpeak) {
      speakup(textToSpeak);
    }*/
  }

  const renderModuleContent = () => {
    switch (moduleState) {
      case MODULE_STATES.TEXT_INPUT:
        return (
          <>
            <div className="llm-module-input-container row">
              <textarea
                onChange={(e) => setPromptString(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendPrompt();
                  }
                }}
                onFocus={onFocus}
                onBlur={onBlur}
                value={promptString}
                className="llm-module-input"
                placeholder="Write your request or question here..."
                rows={1}
              />
              {hasSpeech && <MicIcon isActive={false} handleClick={() => setModuleState(MODULE_STATES.AUDIO_INPUT)} />}
            </div>
            <div className="llm-module-submit-container row">
              <AnimatePresence mode="wait">
                <motion.p
                  className="llm-input-instructions"
                  key={exampleIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1 }}
                >
                  {examples[exampleIndex]}
                </motion.p>
              </AnimatePresence>
              <div className="button-group">
                <Button variant="light" icon="send" handleClick={handleSendPrompt}>
                  Send
                </Button>
              </div>
            </div>
          </>
        );

      case MODULE_STATES.AUDIO_INPUT:
        return (
          <div className="llm-module-audio-container column">
            <div className="llm-module-audio-header row">
              <div className="llm-module-icon-row row">
                <ListeningIcon />
                <p className="llm-state-message">Listening...</p>
              </div>
              <MicIcon
                isActive={true}
                handleClick={() => {
                  setPreviousInputState(MODULE_STATES.TEXT_INPUT);
                  setModuleState(MODULE_STATES.TEXT_INPUT);
                }}
              />
            </div>
            <p className={`llm-speech-text ${isListening ? "interim-text" : ""}`}>{speechText}</p>
          </div>
        );

      case MODULE_STATES.THINKING:
        return (
          <div className="llm-module-icon-row row">
            <ThinkingIcon />
            <p className="llm-state-message">Thinking...</p>
          </div>
        );

      case MODULE_STATES.RESULTS:
        return (
          <div className="llm-module-icon-row llm-results-container row">
            {response !== "" ? (
              <p className="llm-state-message">{response}</p>
            ) : (
              <>
                <CheckIcon />
                <p className="llm-state-message">Request processed successfully!</p>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence mode="wait" initial={false}>
      {!isActive ? (
        <motion.div
          key="icon"
          className="llm-icon-container"
          onClick={() => onToggleLlmActive(true)}
          variants={containerVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <LlmIcon />
        </motion.div>
      ) : (
        <motion.div
          key="module"
          className="llm-container column"
          variants={containerVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <div className="llm-module-header row">
            <LlmIcon variant="view-only" />
            <div className="close-llm" onClick={() => onToggleLlmActive(false)}>
              <div className="bar-1"></div>
              <div className="bar-2"></div>
            </div>
          </div>
          <div className="llm-module-content-container column">
            <AnimatePresence mode="wait">
              <motion.div
                key={moduleState}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="llm-module-content column"
              >
                {renderModuleContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LlmModule;
